import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { LOSS_TYPE_LABEL, LOSS_TYPES, type Claim, type LossType } from "@ti/core";
import { Hourglass, Plus, Receipt, ShieldOff, Zap } from "lucide-react";
import { useMemo, useState } from "react";
import { StatTile } from "@/components/domain/stat";
import { ErrorPanel } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/misc";
import { ClaimSheet, ClaimsTable } from "@/features/coverage/claims-table";
import { FileClaimDialog } from "@/features/coverage/file-claim-dialog";
import { FILED_BY, holderShortName, isOpenClaim, sumMoney } from "@/features/coverage/meta";
import { q } from "@/lib/api";
import { fmtInt, fmtMoney, fmtPct } from "@/lib/format";

interface ClaimsSearch {
  policyId?: string;
  filedBy?: Claim["filedBy"];
  lossType?: LossType;
}

export const Route = createFileRoute("/_app/coverage/claims")({
  validateSearch: (s: Record<string, unknown>): ClaimsSearch => ({
    policyId: typeof s.policyId === "string" && s.policyId ? s.policyId : undefined,
    filedBy: typeof s.filedBy === "string" && s.filedBy in FILED_BY ? (s.filedBy as Claim["filedBy"]) : undefined,
    lossType: typeof s.lossType === "string" && (LOSS_TYPES as readonly string[]).includes(s.lossType) ? (s.lossType as LossType) : undefined,
  }),
  component: ClaimsPage,
});

function ClaimsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const claims = useQuery(q.claims());
  const policies = useQuery(q.policies());
  const org = useQuery(q.org());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filing, setFiling] = useState(false);

  const all = claims.data;
  const list = useMemo(
    () => all?.filter((c) => (!search.policyId || c.policyId === search.policyId) && (!search.filedBy || c.filedBy === search.filedBy) && (!search.lossType || c.lossType === search.lossType)),
    [all, search.policyId, search.filedBy, search.lossType],
  );
  const filtered = Boolean(search.policyId || search.filedBy || search.lossType);
  const selected = all?.find((c) => c.id === selectedId) ?? null;
  const setSearch = (patch: Partial<ClaimsSearch>) => void navigate({ search: (prev: ClaimsSearch) => ({ ...prev, ...patch }), replace: true });

  const kpis = useMemo(() => {
    if (!list) return null;
    const year = new Date().getFullYear();
    const open = list.filter(isOpenClaim);
    const paid = list.filter((c) => c.status === "paid" && new Date(c.createdAt).getFullYear() === year);
    const fast = list.filter((c) => c.fastTrack);
    const manip = list.filter((c) => c.status === "denied" && (c.decision?.reason === "provoked" || c.provoked));
    const currency = list[0]?.loss.currency ?? "USD";
    return {
      open: open.length,
      openLoss: { amount: sumMoney(open, (c) => c.loss.amount), currency },
      paidAmount: { amount: sumMoney(paid, (c) => c.payout?.amount ?? 0), currency },
      paidCount: paid.length,
      fastShare: list.length ? fast.length / list.length : 0,
      fastCount: fast.length,
      manip: manip.length,
      manipLoss: { amount: sumMoney(manip, (c) => c.loss.amount), currency },
    };
  }, [list]);

  const filedByLabel = (k: Claim["filedBy"]) => (k === "customer" ? holderShortName(org.data?.name) : FILED_BY[k].label);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="Open claims" icon={<Hourglass />} value={kpis ? fmtInt(kpis.open) : <Skeleton className="h-6 w-10" />} sub={kpis ? `${fmtMoney(kpis.openLoss)} in reported losses` : null} />
        <StatTile
          label="Paid, year to date"
          icon={<Receipt />}
          value={kpis ? fmtMoney(kpis.paidAmount) : <Skeleton className="h-6 w-16" />}
          sub={kpis ? `${kpis.paidCount} ${kpis.paidCount === 1 ? "claim" : "claims"} paid` : null}
        />
        <StatTile
          label="Fast-track share"
          icon={<Zap />}
          value={kpis ? fmtPct(kpis.fastShare) : <Skeleton className="h-6 w-12" />}
          sub={kpis ? `${kpis.fastCount} of ${list?.length ?? 0} claims settled on objective evidence` : null}
        />
        <StatTile
          label="Denied for manipulation"
          icon={<ShieldOff />}
          value={kpis ? fmtInt(kpis.manip) : <Skeleton className="h-6 w-10" />}
          sub={kpis ? (kpis.manip > 0 ? `${fmtMoney(kpis.manipLoss)} excluded for the claimant` : "No provoked claims") : null}
        />
      </div>

      {claims.error ? (
        <ErrorPanel error={claims.error} reset={() => void claims.refetch()} />
      ) : (
        <Card className="overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
            <div className="min-w-0">
              <h2 className="text-[13px] font-semibold">Incidents and claims</h2>
              <p className="mt-0.5 text-xs text-fg-3">
                First-party claims cover your own losses; third-party claims cover people your agents affected — filed on your protection page, by your team, or opened automatically from detected losses. Claims are decided from the evidence pack by the program's claims handlers or the carrier.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select aria-label="Filter by loss type" value={search.lossType ?? ""} onChange={(e) => setSearch({ lossType: (e.target.value || undefined) as LossType | undefined })} className="w-44">
                <option value="">Any loss type</option>
                {LOSS_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {LOSS_TYPE_LABEL[t]}
                  </option>
                ))}
              </Select>
              <Select aria-label="Filter by who filed" value={search.filedBy ?? ""} onChange={(e) => setSearch({ filedBy: (e.target.value || undefined) as Claim["filedBy"] | undefined })} className="w-40">
                <option value="">Anyone filed</option>
                {(Object.keys(FILED_BY) as Claim["filedBy"][]).map((k) => (
                  <option key={k} value={k}>
                    {filedByLabel(k)}
                  </option>
                ))}
              </Select>
              <Select aria-label="Filter by policy" value={search.policyId ?? ""} onChange={(e) => setSearch({ policyId: e.target.value || undefined })} className="w-56">
                <option value="">All policies</option>
                {policies.data?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
              <Button size="sm" onClick={() => setFiling(true)}>
                <Plus /> File a claim
              </Button>
            </div>
          </div>
          <ClaimsTable
            claims={list}
            loading={claims.isPending}
            showPolicy={!search.policyId}
            onSelect={(c) => setSelectedId(c.id)}
            empty={{
              title: filtered ? "No claims match" : "No claims",
              description: filtered ? "Try clearing the filters." : "Claims filed by your team, by people your agents affected, or prompted from detected losses appear here.",
            }}
          />
        </Card>
      )}

      <ClaimSheet claim={selected} onClose={() => setSelectedId(null)} />
      <FileClaimDialog open={filing} onOpenChange={setFiling} defaultPolicyId={search.policyId} onFiled={(c) => setSelectedId(c.id)} />
    </div>
  );
}
